<template>
  <div class="pitchcrafter-results">
    <v-container fluid class="px-6 py-4">
      <!-- Header Section -->
      <div class="header-section mb-6">
        <div class="header-content">
          <h1 class="page-title">
            <v-icon large class="mr-3" color="#667eea">mdi-sparkles</v-icon>
            Generated Pitches
          </h1>
          <div class="header-stats">
            <v-chip color="primary" text-color="white" class="mr-2">
              <v-icon left small>mdi-file-document</v-icon>
              {{ pitchResults.length }} Results
            </v-chip>
          </div>
        </div>
      </div>

      <!-- Filters Section -->
      <v-card class="filters-card mb-6" elevation="4">
        <v-card-text class="pa-6">
          <v-row>
            <v-col cols="12" sm="6" md="3">
              <v-text-field
                v-model="filters.search"
                label="Search results..."
                prepend-inner-icon="mdi-magnify"
                outlined
                dense
                clearable
                @input="applyFilters"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-select
                v-model="filters.outputType"
                label="Output Type"
                :items="outputTypeOptions"
                outlined
                dense
                clearable
                @change="applyFilters"
              ></v-select>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-select
                v-model="filters.quality"
                label="Quality Score"
                :items="qualityOptions"
                outlined
                dense
                clearable
                @change="applyFilters"
              ></v-select>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-select
                v-model="filters.dateRange"
                label="Date Range"
                :items="dateRangeOptions"
                outlined
                dense
                clearable
                @change="applyFilters"
              ></v-select>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Results Grid -->
      <v-row>
        <v-col
          v-for="result in filteredResults"
          :key="result.id"
          cols="12"
          md="6"
          lg="4"
        >
          <v-card class="result-card" elevation="4" hover>
            <!-- Card Header -->
            <v-card-title class="card-header pa-4">
              <div class="card-header-content">
                <div class="client-info">
                  <div class="client-avatar">
                    {{ result.proposal?.client_name?.charAt(0).toUpperCase() || 'N' }}
                  </div>
                  <div class="client-details">
                    <div class="client-name">{{ result.proposal?.client_name || 'Unknown Client' }}</div>
                    <div class="client-company">{{ result.proposal?.client_company || 'Unknown Company' }}</div>
                  </div>
                </div>
                <div class="card-actions">
                  <v-menu bottom left>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        small
                        v-bind="attrs"
                        v-on="on"
                      >
                        <v-icon>mdi-dots-vertical</v-icon>
                      </v-btn>
                    </template>
                    <v-list>
                      <v-list-item @click="viewResult(result)">
                        <v-list-item-icon>
                          <v-icon>mdi-eye</v-icon>
                        </v-list-item-icon>
                        <v-list-item-title>View Details</v-list-item-title>
                      </v-list-item>
                      <v-list-item @click="downloadResult(result)" v-if="result.file_path">
                        <v-list-item-icon>
                          <v-icon>mdi-download</v-icon>
                        </v-list-item-icon>
                        <v-list-item-title>Download</v-list-item-title>
                      </v-list-item>
                      <v-list-item @click="regenerateResult(result)">
                        <v-list-item-icon>
                          <v-icon>mdi-refresh</v-icon>
                        </v-list-item-icon>
                        <v-list-item-title>Regenerate</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </div>
              </div>
            </v-card-title>

            <!-- Card Content -->
            <v-card-text class="pa-4">
              <!-- Type & Quality Score -->
              <div class="result-meta mb-3">
                <div class="type-badge">
                  <v-icon :color="getTypeColor(result.output_type)" class="mr-1">
                    {{ getTypeIcon(result.output_type) }}
                  </v-icon>
                  {{ getTypeText(result.output_type) }}
                </div>
                <div class="quality-score">
                  <v-progress-circular
                    :value="(result.personalization_score || 0) * 100"
                    :color="getQualityColor(result.personalization_score)"
                    size="32"
                    width="3"
                  >
                    <span class="score-text">{{ Math.round((result.personalization_score || 0) * 100) }}</span>
                  </v-progress-circular>
                </div>
              </div>

              <!-- Content Preview -->
              <div class="content-preview">
                <h4 class="preview-title">Content Preview</h4>
                <p class="preview-text">
                  {{ truncateText(result.generated_content || 'No content available', 150) }}
                </p>
              </div>

              <!-- Value Proposition -->
              <div v-if="result.value_proposition" class="value-proposition">
                <h4 class="section-title">
                  <v-icon small class="mr-1">mdi-lightbulb-outline</v-icon>
                  Value Proposition
                </h4>
                <p class="section-content">{{ truncateText(result.value_proposition, 100) }}</p>
              </div>

              <!-- Key Features -->
              <div class="key-features">
                <v-chip
                  v-if="result.personalization_score >= 0.8"
                  color="success"
                  text-color="white"
                  small
                  class="mr-1 mb-1"
                >
                  <v-icon left small>mdi-star</v-icon>
                  High Quality
                </v-chip>
                <v-chip
                  v-if="result.case_studies"
                  color="info"
                  text-color="white"
                  small
                  class="mr-1 mb-1"
                >
                  <v-icon left small>mdi-chart-box</v-icon>
                  Case Studies
                </v-chip>
                <v-chip
                  v-if="result.file_path"
                  color="warning"
                  text-color="white"
                  small
                  class="mr-1 mb-1"
                >
                  <v-icon left small>mdi-download</v-icon>
                  Downloadable
                </v-chip>
              </div>
            </v-card-text>

            <!-- Card Footer -->
            <v-card-actions class="pa-4 pt-0">
              <div class="card-footer">
                <div class="creation-date">
                  <v-icon small class="mr-1">mdi-calendar</v-icon>
                  {{ formatDate(result.created_at) }}
                </div>
                <div class="action-buttons">
                  <v-btn
                    small
                    color="primary"
                    outlined
                    @click="viewResult(result)"
                  >
                    <v-icon left small>mdi-eye</v-icon>
                    View
                  </v-btn>
                  <v-btn
                    v-if="result.file_path"
                    small
                    color="success"
                    @click="downloadResult(result)"
                  >
                    <v-icon left small>mdi-download</v-icon>
                    Download
                  </v-btn>
                </div>
              </div>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>

      <!-- Empty State -->
      <div v-if="filteredResults.length === 0 && !isLoading" class="empty-state">
        <v-card class="text-center pa-8" elevation="2">
          <v-icon size="80" color="grey lighten-1">mdi-sparkles-outline</v-icon>
          <h3 class="mt-4 mb-2">No Generated Pitches Found</h3>
          <p class="text--secondary mb-4">
            {{ hasActiveFilters ? 'Try adjusting your filters.' : 'Create your first proposal to generate pitches.' }}
          </p>
          <v-btn
            v-if="!hasActiveFilters"
            color="primary"
            to="/agents/pitchcrafter-ai/proposals"
          >
            Create Proposal
          </v-btn>
        </v-card>
      </div>

      <!-- Loading State -->
      <div v-if="isLoading" class="text-center pa-8">
        <v-progress-circular
          indeterminate
          color="primary"
          size="64"
        ></v-progress-circular>
        <p class="mt-4">Loading generated pitches...</p>
      </div>

      <!-- View Result Dialog -->
      <v-dialog v-model="showViewDialog" max-width="900px">
        <v-card v-if="selectedResult">
          <v-card-title class="headline">
            <v-icon left color="primary">mdi-sparkles</v-icon>
            Generated Pitch Details
          </v-card-title>
          <v-card-text class="pa-6">
            <v-row>
              <v-col cols="12" md="6">
                <h4>Client Information</h4>
                <p><strong>Name:</strong> {{ selectedResult.proposal?.client_name }}</p>
                <p><strong>Company:</strong> {{ selectedResult.proposal?.client_company }}</p>
                <p><strong>Industry:</strong> {{ selectedResult.proposal?.industry }}</p>
              </v-col>
              <v-col cols="12" md="6">
                <h4>Generation Details</h4>
                <p><strong>Type:</strong> {{ getTypeText(selectedResult.output_type) }}</p>
                <p><strong>Quality Score:</strong> {{ Math.round((selectedResult.personalization_score || 0) * 100) }}%</p>
                <p><strong>Created:</strong> {{ formatDate(selectedResult.created_at) }}</p>
              </v-col>
            </v-row>
            <v-divider class="my-4"></v-divider>
            <div v-if="selectedResult.generated_content">
              <h4>Generated Content</h4>
              <div class="content-display">
                {{ selectedResult.generated_content }}
              </div>
            </div>
          </v-card-text>
          <v-card-actions class="pa-6">
            <v-spacer></v-spacer>
            <v-btn text @click="showViewDialog = false">Close</v-btn>
            <v-btn
              v-if="selectedResult.file_path"
              color="primary"
              @click="downloadResult(selectedResult)"
            >
              <v-icon left>mdi-download</v-icon>
              Download
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </div>
</template>

<script>
import axios from "@/axios";

export default {
  name: "PitchCrafterAIResults",
  data() {
    return {
      isLoading: false,
      pitchResults: [],
      showViewDialog: false,
      selectedResult: null,
      filters: {
        search: '',
        outputType: '',
        quality: '',
        dateRange: ''
      },
      outputTypeOptions: [
        { text: 'Email', value: 'email' },
        { text: 'Presentation', value: 'presentation' },
        { text: 'Document', value: 'document' }
      ],
      qualityOptions: [
        { text: 'High (80%+)', value: 'high' },
        { text: 'Medium (60-79%)', value: 'medium' },
        { text: 'Low (<60%)', value: 'low' }
      ],
      dateRangeOptions: [
        { text: 'Today', value: 'today' },
        { text: 'This Week', value: 'week' },
        { text: 'This Month', value: 'month' },
        { text: 'Last 3 Months', value: '3months' }
      ]
    };
  },
  computed: {
    filteredResults() {
      let results = [...this.pitchResults];
      
      // Apply search filter
      if (this.filters.search) {
        const search = this.filters.search.toLowerCase();
        results = results.filter(result => 
          result.proposal?.client_name?.toLowerCase().includes(search) ||
          result.proposal?.client_company?.toLowerCase().includes(search) ||
          result.generated_content?.toLowerCase().includes(search)
        );
      }
      
      // Apply output type filter
      if (this.filters.outputType) {
        results = results.filter(result => result.output_type === this.filters.outputType);
      }
      
      // Apply quality filter
      if (this.filters.quality) {
        results = results.filter(result => {
          const score = (result.personalization_score || 0) * 100;
          switch (this.filters.quality) {
            case 'high': return score >= 80;
            case 'medium': return score >= 60 && score < 80;
            case 'low': return score < 60;
            default: return true;
          }
        });
      }
      
      return results;
    },
    hasActiveFilters() {
      return Object.values(this.filters).some(value => value !== '');
    }
  },
  async mounted() {
    await this.fetchPitchResults();
  },
  methods: {
    async fetchPitchResults() {
      try {
        this.isLoading = true;
        const response = await axios.get('pitch-results');
        this.pitchResults = response.data.data;
      } catch (error) {
        console.error('Error fetching pitch results:', error);
        this.$store.dispatch('errors/setErrors', {
          general: ['Failed to load pitch results. Please try again.']
        });
      } finally {
        this.isLoading = false;
      }
    },
    viewResult(result) {
      this.selectedResult = result;
      this.showViewDialog = true;
    },
    async downloadResult(result) {
      if (!result.file_path) return;
      
      try {
        const response = await axios.get(`pitch-results/${result.id}/download`, {
          responseType: 'blob'
        });
        
        // Create download link
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `pitch-${result.id}.${this.getFileExtension(result.output_type)}`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        
        this.$store.dispatch('settings/showSnackbar', {
          message: 'File downloaded successfully!',
          color: 'success'
        });
      } catch (error) {
        console.error('Error downloading file:', error);
        this.$store.dispatch('errors/setErrors', {
          general: ['Failed to download file. Please try again.']
        });
      }
    },
    async regenerateResult(result) {
      if (!confirm('Are you sure you want to regenerate this pitch?')) return;
      
      try {
        await axios.post(`proposals/${result.proposal.id}/generate`);
        await this.fetchPitchResults();
        
        this.$store.dispatch('settings/showSnackbar', {
          message: 'Pitch regenerated successfully!',
          color: 'success'
        });
      } catch (error) {
        console.error('Error regenerating pitch:', error);
        this.$store.dispatch('errors/setErrors', {
          general: ['Failed to regenerate pitch. Please try again.']
        });
      }
    },
    applyFilters() {
      // Filters are applied through computed property
    },
    getTypeColor(type) {
      const colors = {
        email: '#667eea',
        presentation: '#51cf66',
        document: '#ff9500'
      };
      return colors[type] || '#667eea';
    },
    getTypeIcon(type) {
      const icons = {
        email: 'mdi-email',
        presentation: 'mdi-presentation',
        document: 'mdi-file-document'
      };
      return icons[type] || 'mdi-file-document';
    },
    getTypeText(type) {
      const texts = {
        email: 'Email',
        presentation: 'Presentation',
        document: 'Document'
      };
      return texts[type] || type;
    },
    getQualityColor(score) {
      const percentage = (score || 0) * 100;
      if (percentage >= 80) return 'success';
      if (percentage >= 60) return 'warning';
      return 'error';
    },
    getFileExtension(type) {
      const extensions = {
        email: 'txt',
        presentation: 'pptx',
        document: 'pdf'
      };
      return extensions[type] || 'txt';
    },
    truncateText(text, length) {
      if (!text) return '';
      return text.length > length ? text.substring(0, length) + '...' : text;
    },
    formatDate(date) {
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  }
};
</script>

<style scoped>
.pitchcrafter-results {
  min-height: calc(100vh - 64px);
}

.header-section {
  background: white;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 8px 32px rgba(102, 126, 234, 0.1);
  border: 1px solid rgba(102, 126, 234, 0.1);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 20px;
}

.page-title {
  font-size: 32px;
  font-weight: 700;
  color: #2c3e50;
  margin: 0;
  display: flex;
  align-items: center;
}

.filters-card {
  border-radius: 16px !important;
  border: 1px solid rgba(102, 126, 234, 0.1);
}

.result-card {
  border-radius: 16px !important;
  border: 1px solid rgba(102, 126, 234, 0.1);
  transition: all 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.result-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 40px rgba(102, 126, 234, 0.15) !important;
}

.card-header {
  border-bottom: 1px solid rgba(102, 126, 234, 0.1);
}

.card-header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.client-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.client-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #667eea;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 16px;
}

.client-details {
  flex: 1;
}

.client-name {
  font-weight: 600;
  color: #2c3e50;
  line-height: 1.2;
  font-size: 14px;
}

.client-company {
  font-size: 12px;
  color: #64748b;
  line-height: 1.2;
}

.result-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.type-badge {
  display: flex;
  align-items: center;
  font-weight: 500;
  color: #2c3e50;
  font-size: 14px;
}

.quality-score {
  position: relative;
}

.score-text {
  font-size: 10px;
  font-weight: 600;
  color: #2c3e50;
}

.content-preview {
  margin-bottom: 16px;
}

.preview-title {
  font-size: 14px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 8px;
}

.preview-text {
  font-size: 13px;
  color: #64748b;
  line-height: 1.4;
  margin: 0;
}

.value-proposition {
  margin-bottom: 16px;
}

.section-title {
  font-size: 13px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 6px;
  display: flex;
  align-items: center;
}

.section-content {
  font-size: 12px;
  color: #64748b;
  line-height: 1.4;
  margin: 0;
}

.key-features {
  margin-bottom: 16px;
}

.card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.creation-date {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: #64748b;
}

.action-buttons {
  display: flex;
  gap: 8px;
}

.empty-state {
  margin-top: 40px;
}

.content-display {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 16px;
  margin-top: 8px;
  white-space: pre-wrap;
  font-family: monospace;
  font-size: 13px;
  line-height: 1.4;
  max-height: 400px;
  overflow-y: auto;
}

/* Responsive Design */
@media (max-width: 960px) {
  .header-content {
    flex-direction: column;
    text-align: center;
  }
  
  .page-title {
    font-size: 28px;
    justify-content: center;
  }
}

@media (max-width: 600px) {
  .header-section {
    padding: 20px;
  }
  
  .page-title {
    font-size: 24px;
    flex-direction: column;
    gap: 8px;
  }
  
  .client-info {
    flex-direction: column;
    gap: 8px;
    text-align: center;
  }
  
  .client-avatar {
    width: 32px;
    height: 32px;
    font-size: 14px;
  }
  
  .card-footer {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }
}
</style>